import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dimensionApi, ListDimensionsParams } from '../services/accountingApi';
import {
  DimensionCreate,
  DimensionUpdate,
  DimensionValueCreate,
  DimensionValueUpdate,
} from '../types';

export const DIMENSIONS_QUERY_KEY = 'accounting-dimensions';
export const DIMENSION_VALUES_QUERY_KEY = 'accounting-dimension-values';

export function useDimensions(params?: ListDimensionsParams) {
  return useQuery({
    queryKey: [DIMENSIONS_QUERY_KEY, params],
    queryFn: () => dimensionApi.list(params),
  });
}

export function useDimension(id: string) {
  return useQuery({
    queryKey: [DIMENSIONS_QUERY_KEY, id],
    queryFn: () => dimensionApi.get(id),
    enabled: !!id,
  });
}

export function useDimensionValues(dimensionId: string, includeInactive?: boolean) {
  return useQuery({
    queryKey: [DIMENSION_VALUES_QUERY_KEY, dimensionId, { includeInactive }],
    queryFn: () => dimensionApi.listValues(dimensionId, includeInactive),
    enabled: !!dimensionId,
  });
}

export function useCreateDimension() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DimensionCreate) => dimensionApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DIMENSIONS_QUERY_KEY] });
    },
  });
}

export function useUpdateDimension() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DimensionUpdate }) =>
      dimensionApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DIMENSIONS_QUERY_KEY] });
    },
  });
}

export function useDeleteDimension() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dimensionApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DIMENSIONS_QUERY_KEY] });
    },
  });
}

export function useCreateDimensionValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dimensionId, data }: { dimensionId: string; data: DimensionValueCreate }) =>
      dimensionApi.createValue(dimensionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DIMENSION_VALUES_QUERY_KEY] });
    },
  });
}

export function useUpdateDimensionValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ valueId, data }: { valueId: string; data: DimensionValueUpdate }) =>
      dimensionApi.updateValue(valueId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DIMENSION_VALUES_QUERY_KEY] });
    },
  });
}

export function useDeleteDimensionValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (valueId: string) => dimensionApi.deleteValue(valueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DIMENSION_VALUES_QUERY_KEY] });
    },
  });
}
